import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import Colors from "../../theme/colors.json";
import { MenuItem, Select } from "@mui/material";
import { pushNotification } from "../../redux/actions";
import CONSTANTS, { Chart_Errors } from "../../constants/constants";
import { useDispatch } from "react-redux";
import { getServiceMangerPerformance } from "../../services/ChartService";
import NoData from "./NoData";

const useStyles = makeStyles((theme) => ({
  outerContainer: {
    height: "75%",
    overflowY: "auto",
    overflowX: "hidden",
    "&::-webkit-scrollbar": {
      width: "0.4rem",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "#f1f1f1",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#888",
      borderRadius: "6px",
    },
    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#555",
    },
  },
  barGraph: {
    display: "flex",
    flexDirection: "column",
    gap: "1rem",
    width: "95%",
  },
  bar: {
    display: "flex",
    alignItems: "center",
    height: "10px",
  },
  barLabel: {
    fontWeight: "600",
    fontSize: theme.fontSizes.SMALL,
    textTransform: "capitalize",
  },
  barAmountContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  commissionBar: {
    backgroundColor: Colors.GRAPH_TERITARY_COLOR,
    color: "transparent",
    textAlign: "center",
    height: "100%",
    transition: "width 0.5s ease-in-out",
  },
  premiumBar: {
    backgroundColor: Colors.GRAPH_PRIMARY_COLOR,
    color: "transparent",
    textAlign: "center",
    height: "100%",
    transition: "width 0.5s ease-in-out",
  },
  barValueCommission: {
    color: Colors.GRAPH_TERITARY_COLOR,
    fontSize: theme.fontSizes.SMALL,
  },
  barValuePremium: {
    color: Colors.GRAPH_PRIMARY_COLOR,
    fontSize: theme.fontSizes.SMALL,
  },
  grpahContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "30px",
  },
  headerText: {
    fontSize: theme.fontSizes.LARGE,
    fontWeight: 600,
  },
  ServiceManagerGraph: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
  },
  circle: {
    width: 7,
    height: 7,
    borderRadius: "50%",
    margin: theme.spacing(1),
  },
  green: {
    backgroundColor: Colors.GRAPH_TERITARY_COLOR,
  },
  blue: {
    backgroundColor: Colors.GRAPH_PRIMARY_COLOR,
  },
  legendContainer: {
    display: "flex",
    justifyContent: "center",
  },
  legendSubContainer: {
    display: "flex",
  },
  legendtext: {
    color: "#999999",
    fontSize: theme.fontSizes.SMALL,
  },
  selectStyles: {
    color: theme.palette.primary.main,
    fontSize: theme.fontSizes.SMALL,
    height: "1.6rem",
    minWidth: "5rem",
    "& .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: theme.palette.primary.main,
    },
    "& .MuiSvgIcon-root": {
      fill: `${theme.palette.primary.main} !important`,
    },
  },
  filterContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    marginBottom: "3%",
  },
  filtersSection: {
    display: "flex",
    gap: "3%",
    // justifyContent: "space-around",
    // width: "50%",
  },
}));

const ServiceManagerPerformanceChart = ({ years, months }) => {
  const classes = useStyles();

  const dispatch = useDispatch();

  const [selectedYear, setSelectedYear] = useState(
    years[0]?.value || new Date().getFullYear()
  );
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [chartData, setChartData] = useState<any>([]);
  const [maxValue, setMaxValue] = useState(0); // State to hold the maximum value

  const [isLoading, setIsLoading] = useState(false);

  // Adjust the scaling factor to make the bars wider
  const scalingFactor = 90; // Change this value as needed

  const handleGetServiceManagerPerformance = async () => {
    setIsLoading(true);
    const response = await getServiceMangerPerformance(
      selectedMonth,
      selectedYear
    );
    if (response?.success) {
      setChartData(response?.data);
      setIsLoading(false);
      // dispatch(
      //   pushNotification({
      //     isOpen: true,
      //     message: response.message,
      //     type: CONSTANTS.SUCCESS,
      //   })
      // );
    } else {
      setChartData([]);
      setIsLoading(false);
      // dispatch(
      //   pushNotification({
      //     isOpen: true,
      //     message: response.message,
      //     type: CONSTANTS.ERROR,
      //   })
      // );
    }
  };

  useEffect(() => {
    handleGetServiceManagerPerformance();
  }, [selectedYear, selectedMonth]);

  useEffect(() => {
    if (chartData?.length > 0) {
      const maxValue = Math.max(
        ...chartData?.map((item) =>
          Math.max(item?.totalNetCommissionAmount, item?.totalNetPremium)
        )
      );
      setMaxValue(maxValue);
    }
  }, [chartData]);

  return (
    <div className={classes.ServiceManagerGraph}>
      <div className={classes.filterContainer}>
        <div className={classes.headerText}>Service Manager Performance</div>
        <div className={classes.filtersSection}>
          <Select
            size="small"
            className={classes.selectStyles}
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {years?.map((item, index) => {
              return (
                <MenuItem key={index} value={item?.value}>
                  {item?.title}
                </MenuItem>
              );
            })}
          </Select>
          <Select
            size="small"
            className={classes.selectStyles}
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            {months?.map((item, index) => {
              return (
                <MenuItem key={index} value={item?.value}>
                  {item?.title}
                </MenuItem>
              );
            })}
          </Select>
        </div>
      </div>
      {chartData?.length > 0 && !isLoading && (
        <>
          <div className={classes.outerContainer}>
            <div className={classes.barGraph}>
              {chartData
                ?.filter(
                  (item) =>
                    item?.totalNetCommissionAmount !== 0 ||
                    item?.totalNetPremium !== 0
                )
                ?.map((item, index) => (
                  <div key={index}>
                    <div className={classes.barLabel}>{item?.name}</div>
                    <div className={classes.bar}>
                      <div
                        className={classes.commissionBar}
                        style={{
                          width: `${
                            Math.min(
                              (item?.totalNetCommissionAmount / maxValue) *
                                scalingFactor,
                              100
                            ) || 0
                          }%`,
                        }}
                      >
                        Commission
                      </div>
                      &nbsp;
                      <div
                        className={classes.premiumBar}
                        style={{
                          width: `${
                            Math.min(
                              (item?.totalNetPremium / maxValue) *
                                scalingFactor,
                              100
                            ) || 0
                          }%`,
                        }}
                      >
                        Premium
                      </div>
                    </div>
                    <div
                      className={classes.barAmountContainer}
                      style={{
                        width: `${
                          Math.min(
                            (item?.totalNetCommissionAmount / maxValue) *
                              scalingFactor +
                              (item?.totalNetPremium / maxValue) *
                                scalingFactor,
                            100
                          ) || 0
                        }%`,
                      }}
                    >
                      <div className={classes.barValueCommission}>
                        {item?.totalNetCommissionAmount}
                      </div>
                      <div className={classes.barValuePremium}>
                        {item?.totalNetPremium}
                      </div>
                    </div>
                  </div>
                ))}
            </div>

            {/* <div className={classes.barGraph}>
          {chartData?.map((item, index) => {
            const itemMaxValue = Math.max(
              item.totalNetCommissionAmount,
              item.totalNetPremium
            );
 
            return (
              <div key={index}>
                <div className={classes.barLabel}>{item?.name}</div>
                <div className={classes.bar}>
                  <div
                    className={classes.commissionBar}
                    style={{
                      width:
                        item.totalNetCommissionAmount !== 0
                          ? `${
                              (item?.totalNetCommissionAmount / itemMaxValue) *
                              100
                            }%`
                          : "0%",
                    }}
                  >
                    Commission
                  </div>
                  &nbsp;
                  <div
                    className={classes.premiumBar}
                    style={{
                      width:
                        item.totalNetPremium !== 0
                          ? `${(item.totalNetPremium / itemMaxValue) * 100}%`
                          : "0%",
                    }}
                  >
                    Premium
                  </div>
                </div>
                <div
                  className={classes.barAmountContainer}
                  style={{
                    width: `${Math.min(
                      (item.totalNetCommissionAmount / itemMaxValue) * 100 +
                        (item.totalNetPremium / itemMaxValue) * 100,
                      100
                    )}%`,
                  }}
                >
                  {item.totalNetCommissionAmount !== 0 && (
                    <div className={classes.barValueCommission}>
                      {item.totalNetCommissionAmount}
                    </div>
                  )}
 
                  {item.totalNetPremium !== 0 && (
                    <div className={classes.barValuePremium}>
                      {item.totalNetPremium}
                    </div>
                  )}
 
                  {item.totalNetCommissionAmount === 0 &&
                    item.totalNetPremium === 0 && (
                      <div>
                        <span className={classes.barValueCommission}>0</span>{" "}
                        <span className={classes.barValuePremium}>0</span>
                      </div>
                    )}
                </div>
              </div>
            );
          })}
        </div> */}
          </div>
          <div className={classes.legendContainer}>
            <div className={classes.legendSubContainer}>
              <div className={`${classes.circle} ${classes.green}`}></div>
              <div className={classes.legendtext}>Commission</div>
            </div>
            <div className={classes.legendSubContainer}>
              <div className={`${classes.circle} ${classes.blue}`}></div>
              <div className={classes.legendtext}>Premium</div>
            </div>
          </div>
        </>
      )}

      {chartData?.length === 0 && !isLoading && (
        <NoData errorLabel={Chart_Errors.serviceManger_error} />
      )}
    </div>
  );
};

export default ServiceManagerPerformanceChart;
